import type { FieldPath, FieldValues, UseFormSetError } from 'react-hook-form';

import { ApiRequestError } from '@/shared/lib/api';
import { messages } from '@/shared/i18n';

const VALIDATION_ERROR_CODE = 'validation_error';

type ApiValidationError = ApiRequestError & {
  status: 400;
  code: typeof VALIDATION_ERROR_CODE;
};

export function isApiValidationError(err: unknown): err is ApiValidationError {
  return (
    err instanceof ApiRequestError &&
    err.status === 400 &&
    err.code === VALIDATION_ERROR_CODE
  );
}

/** Flatten string messages from API `error.details`. */
export function extractValidationDetailMessages(details: unknown): string[] {
  if (!details || typeof details !== 'object' || Array.isArray(details)) {
    return [];
  }
  return Object.values(details as Record<string, unknown>).filter(
    (value): value is string => typeof value === 'string' && value.trim().length > 0,
  );
}

/** User-facing banner copy for validation failures (not the backend top-level message). */
export function validationErrorBannerMessage(): string {
  return messages.errors.validationBody;
}

/** Build snake_case API key → camelCase form key map. */
export function createSnakeToCamelKeyMap<T extends string>(
  keys: readonly T[],
): Record<string, T> {
  const map: Record<string, T> = {};
  for (const key of keys) {
    const snake = key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
    map[snake] = key;
  }
  return map;
}

export const SIGNUP_API_KEY_MAP = createSnakeToCamelKeyMap([
  'fullName',
  'email',
  'password',
] as const);

export const ORGANISATION_IDENTITY_API_KEY_MAP = createSnakeToCamelKeyMap([
  'legalEntityName',
  'abn',
  'ndisProviderNumber',
  'primarySector',
] as const);

export const ORGANISATION_PROFILE_API_KEY_MAP = createSnakeToCamelKeyMap([
  'tradingName',
  'legalEntityName',
  'abn',
  'ndisProviderNumber',
  'primarySector',
] as const);

export const SERVICE_SITE_API_KEY_MAP = createSnakeToCamelKeyMap([
  'name',
  'address',
  'phone',
  'siteManagerName',
  'externalId',
  'geofenceRadiusMeters',
  'primaryServiceType',
  'departmentName',
  'timezone',
] as const);

export const TEAM_INVITE_API_KEY_MAP = {
  full_name: 'fullName',
  email: 'email',
  intended_role_id: 'roleId',
  assigned_site_id: 'siteId',
} as const;

/** Backend `details` keys for first intake / hub create (explicit map — spread loses keyof for TS). */
export const FIRST_INTAKE_API_KEY_MAP = {
  full_name: 'fullName',
  ndis_number: 'ndisNumber',
  date_of_birth: 'dateOfBirth',
  guardian_name: 'guardianName',
  guardian_email: 'guardianEmail',
  primary_diagnosis: 'primaryDiagnosis',
  allergies: 'allergies',
  risk_flags: 'riskFlags',
  primary_care_site_id: 'primaryCareSiteId',
  primary_care_team_id: 'primaryCareTeamId',
  additional_site_ids: 'additionalCareSiteIds',
} as const;

/** Profile edit form field map for API validation errors. */
export const PATIENT_PROFILE_API_KEY_MAP = {
  full_name: 'fullName',
  ndis_number: 'ndisNumber',
  date_of_birth: 'dateOfBirth',
  guardian_name: 'guardianName',
  guardian_email: 'guardianEmail',
  primary_diagnosis: 'primaryDiagnosis',
  allergies: 'allergies',
  risk_flags: 'riskFlags',
  primary_care_site_id: 'primaryCareSiteId',
  primary_care_team_id: 'primaryCareTeamId',
  additional_site_ids: 'additionalCareSiteIds',
  monitoring_status: 'monitoringStatus',
} as const;

export const WORKFORCE_EDIT_STAFF_API_KEY_MAP = {
  legal_full_name: 'first_name',
  email: 'email',
  phone_number: 'phone_number',
  date_of_birth: 'date_of_birth',
  residential_address: 'residential_address',
  emergency_contact_name: 'emergency_contact_name',
  emergency_contact_number: 'emergency_contact_number',
  role_name: 'role_name',
  assigned_site_id: 'assigned_site_id',
  additional_site_ids: 'additional_site_ids',
  employment_type: 'employment_type',
  start_date: 'start_date',
} as const;

/** Apply server validation details when `err` is a 400 validation_error. */
export function handleApiValidationError<TFieldValues extends FieldValues>(
  err: unknown,
  setError: UseFormSetError<TFieldValues>,
  keyMap?: Partial<Record<string, FieldPath<TFieldValues>>>,
): boolean {
  if (!(err instanceof ApiRequestError) || !isApiValidationError(err)) {
    return false;
  }
  applyApiValidationErrorsToForm(setError, err.details, keyMap);
  return true;
}

/** Maps API validation `details` onto react-hook-form field paths via `setError`. */
export function applyApiValidationErrorsToForm<TFieldValues extends FieldValues>(
  setError: UseFormSetError<TFieldValues>,
  details: unknown,
  keyMap?: Partial<Record<string, FieldPath<TFieldValues>>>,
): void {
  if (!details || typeof details !== 'object' || Array.isArray(details)) {
    return;
  }
  for (const [key, value] of Object.entries(details as Record<string, unknown>)) {
    if (typeof value !== 'string' || !value.trim()) {
      continue;
    }
    const path = (keyMap?.[key] ?? key) as FieldPath<TFieldValues>;
    setError(path, { type: 'server', message: value });
  }
}
